import { vec2 } from "gl-matrix"
import { Rectangle } from "@/core/types"
import {
	createHealingCoordinateTransformContext,
	getHealingCoordinateJacobian,
	HealingCoordinateProjection,
	HealingCoordinateTransformContext,
	HealingCoordinateTransformOptions,
	projectHealingImageCoordToOutputWithJacobian,
} from "./healing-coordinate-transform"

export type HealingTransformBoundsOptions = HealingCoordinateTransformOptions

export interface HealingTransformBoundsContext extends HealingCoordinateTransformContext {
	boundsSafe: boolean
}

export interface ScissorBounds {
	x: number
	y: number
	width: number
	height: number
}

const MAX_SUBDIVISION_DEPTH = 8
const MAX_BOUNDARY_POINTS = 1024
const CURVE_TOLERANCE_PIXELS = 0.25
const MAX_SEGMENT_LENGTH_PIXELS = 96
const SCISSOR_PADDING_PIXELS = 3

const hasConsistentJacobian = (options: HealingCoordinateTransformOptions) => {
	let sign = 0
	for (let y = 0; y <= 4; y++) {
		for (let x = 0; x <= 4; x++) {
			const jacobian = getHealingCoordinateJacobian([x / 4, y / 4], options)
			if (!jacobian) return false
			const currentSign = Math.sign(jacobian.determinant)
			if (sign !== 0 && currentSign !== sign) return false
			sign = currentSign
		}
	}
	return sign !== 0
}

export const createHealingTransformBoundsContext = (
	options: HealingTransformBoundsOptions,
): HealingTransformBoundsContext => {
	const transformContext = createHealingCoordinateTransformContext(options)
	return {
		...transformContext,
		boundsSafe: hasConsistentJacobian(transformContext),
	}
}

const pointToSegmentDistancePixels = (point: vec2, start: vec2, end: vec2, size: vec2) => {
	const segmentX = (end[0] - start[0]) * size[0]
	const segmentY = (end[1] - start[1]) * size[1]
	const pointX = (point[0] - start[0]) * size[0]
	const pointY = (point[1] - start[1]) * size[1]
	const lengthSquared = segmentX * segmentX + segmentY * segmentY
	if (lengthSquared === 0) return Math.hypot(pointX, pointY)
	const t = Math.max(0, Math.min(1, (pointX * segmentX + pointY * segmentY) / lengthSquared))
	return Math.hypot(pointX - segmentX * t, pointY - segmentY * t)
}

/** Returns ordered, adaptively sampled output-space points around a stable-image rectangle. */
export const getHealingTransformRectBoundary = (
	destinationRect: Rectangle,
	context: HealingTransformBoundsContext,
): vec2[] | null => {
	if (!context.boundsSafe) return null

	const { x, y, width, height } = destinationRect
	const corners: vec2[] = [
		[x, y],
		[x + width, y],
		[x + width, y + height],
		[x, y + height],
	]
	const projections: HealingCoordinateProjection[] = []
	let previous: vec2 | undefined
	for (const corner of corners) {
		const projection = projectHealingImageCoordToOutputWithJacobian(
			corner,
			context,
			previous ?? corner,
		)
		if (!projection) return null
		projections.push(projection)
		previous = projection.coord
	}

	const firstProjection = projections[0]
	if (!firstProjection) return null
	const determinantSign = Math.sign(firstProjection.jacobianDeterminant)
	if (determinantSign === 0) return null
	const boundary: vec2[] = [firstProjection.coord]
	let boundaryPointCount = boundary.length

	const subdivide = (
		imageStart: vec2,
		imageEnd: vec2,
		outputStart: vec2,
		outputEnd: vec2,
		depth: number,
	): boolean => {
		const imageMidpoint: vec2 = [
			(imageStart[0] + imageEnd[0]) * 0.5,
			(imageStart[1] + imageEnd[1]) * 0.5,
		]
		const outputSeed: vec2 = [
			(outputStart[0] + outputEnd[0]) * 0.5,
			(outputStart[1] + outputEnd[1]) * 0.5,
		]
		const midpoint = projectHealingImageCoordToOutputWithJacobian(
			imageMidpoint,
			context,
			outputSeed,
		)
		if (!midpoint) return false
		const currentSign = Math.sign(midpoint.jacobianDeterminant)
		if (currentSign === 0 || currentSign !== determinantSign) return false

		const deviation = pointToSegmentDistancePixels(
			midpoint.coord,
			outputStart,
			outputEnd,
			context.outputSize,
		)
		const segmentLength = Math.hypot(
			(outputEnd[0] - outputStart[0]) * context.outputSize[0],
			(outputEnd[1] - outputStart[1]) * context.outputSize[1],
		)
		const needsSubdivision =
			context.nonlinear &&
			(deviation > CURVE_TOLERANCE_PIXELS || segmentLength > MAX_SEGMENT_LENGTH_PIXELS)
		if (needsSubdivision) {
			if (depth >= MAX_SUBDIVISION_DEPTH) return false
			if (!subdivide(imageStart, imageMidpoint, outputStart, midpoint.coord, depth + 1))
				return false
			if (!subdivide(imageMidpoint, imageEnd, midpoint.coord, outputEnd, depth + 1))
				return false
			return true
		}

		if (boundaryPointCount + 2 > MAX_BOUNDARY_POINTS) return false
		boundary.push(midpoint.coord, outputEnd)
		boundaryPointCount += 2
		return true
	}

	for (let index = 0; index < corners.length; index++) {
		const next = (index + 1) % corners.length
		const imageStart = corners[index]
		const imageEnd = corners[next]
		const outputStart = projections[index]?.coord
		const outputEnd = projections[next]?.coord
		if (!imageStart || !imageEnd || !outputStart || !outputEnd) return null
		if (!subdivide(imageStart, imageEnd, outputStart, outputEnd, 0)) return null
	}

	return boundary
}

/** Returns null when a trustworthy bound cannot be calculated and the caller must draw full-screen. */
export const getHealingTransformScissorBounds = (
	destinationRect: Rectangle,
	context: HealingTransformBoundsContext,
): ScissorBounds | null => {
	const boundary = getHealingTransformRectBoundary(destinationRect, context)
	if (!boundary) return null

	let minX = Number.POSITIVE_INFINITY
	let minY = Number.POSITIVE_INFINITY
	let maxX = Number.NEGATIVE_INFINITY
	let maxY = Number.NEGATIVE_INFINITY
	for (const point of boundary) {
		minX = Math.min(minX, point[0])
		minY = Math.min(minY, point[1])
		maxX = Math.max(maxX, point[0])
		maxY = Math.max(maxY, point[1])
	}
	if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null

	const outputWidth = context.outputSize[0]
	const outputHeight = context.outputSize[1]
	const left = Math.max(0, Math.floor(minX * outputWidth) - SCISSOR_PADDING_PIXELS)
	const bottom = Math.max(0, Math.floor(minY * outputHeight) - SCISSOR_PADDING_PIXELS)
	const right = Math.min(outputWidth, Math.ceil(maxX * outputWidth) + SCISSOR_PADDING_PIXELS)
	const top = Math.min(outputHeight, Math.ceil(maxY * outputHeight) + SCISSOR_PADDING_PIXELS)

	return {
		x: left,
		y: bottom,
		width: Math.max(0, right - left),
		height: Math.max(0, top - bottom),
	}
}
